import { useState, useEffect } from "react";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { useCart } from "@/contexts/CartContext";
import { useNavigate } from "react-router-dom";

interface CartDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CartDrawer = ({ open, onOpenChange }: CartDrawerProps) => {
  const { items, removeItem, updateQuantity, totalPrice, totalItems, clearCart } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    onOpenChange(false);
    navigate("/checkout");
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange} shouldScaleBackground={false}>
      <DrawerContent className="bg-background border-t border-border rounded-t-2xl max-h-[85vh]">
        <div className="flex flex-col h-full max-h-[85vh]">
          {/* Header */}
          <div className="px-4 pt-2 pb-3 border-b border-border/50">
            <div className="w-12 h-1.5 rounded-full bg-muted mx-auto mb-3" />
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-primary" />
                Carrello ({totalItems})
              </h2>
              {items.length > 0 && (
                <Button variant="ghost" size="sm" onClick={clearCart} className="text-destructive text-xs">
                  Svuota
                </Button>
              )}
            </div>
          </div>

          {/* Items */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <ShoppingBag className="w-12 h-12 text-muted-foreground mb-3" />
                <p className="text-muted-foreground font-medium">Il carrello è vuoto</p>
                <p className="text-sm text-muted-foreground/70 mt-1">Aggiungi prodotti per procedere</p>
              </div>
            ) : (
              <AnimatePresence>
                {items.map(item => (
                  <motion.div
                    key={item.variant.id}
                    layout
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    className="flex gap-3 p-3 rounded-xl bg-card border border-border/50"
                  >
                    {item.product.cover_image && (
                      <img
                        src={item.product.cover_image}
                        alt={item.product.name}
                        className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-foreground truncate">{item.product.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {item.selectedColor}
                        {item.selectedMemory && ` • ${item.selectedMemory}GB`}
                        {` • ${item.selectedCondition}`}
                      </p>
                      <p className="text-sm font-bold text-foreground mt-1">€{(item.unitPrice * item.quantity).toFixed(2)}</p>
                    </div>
                    <div className="flex flex-col items-end justify-between">
                      <button onClick={() => removeItem(item.variant.id)} className="p-1 text-muted-foreground hover:text-destructive transition-colors">
                        <X className="w-4 h-4" />
                      </button>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => updateQuantity(item.variant.id, item.quantity - 1)}
                          className="w-7 h-7 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-6 text-center text-sm font-medium">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.variant.id, item.quantity + 1)}
                          className="w-7 h-7 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            )}
          </div>

          {/* Footer */}
          {items.length > 0 && (
            <div className="p-4 border-t border-border/50 space-y-3">
              <div className="flex justify-between text-lg font-bold">
                <span>Totale</span>
                <span>€{totalPrice.toFixed(2)}</span>
              </div>
              <Button className="mx-auto h-12 px-8 text-base gap-2 rounded-xl flex" onClick={handleCheckout}>
                Procedi al checkout
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};
